import React from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '../utils/logger';

const wrap: React.CSSProperties = {
  minHeight: '100vh', background: '#f3f4f6', padding: '24px 16px',
  fontFamily: 'system-ui, sans-serif', color: '#111',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const card: React.CSSProperties = {
  maxWidth: 480, width: '100%', background: '#fff', borderRadius: 12,
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: 32, textAlign: 'center',
};
const button: React.CSSProperties = {
  marginTop: 16, padding: '10px 20px', border: 'none', borderRadius: 8,
  background: '#3788d8', color: '#fff', fontSize: 14, cursor: 'pointer',
};

function ErrorFallback() {
  const { t } = useTranslation();
  return (
    <div style={wrap}>
      <div style={card}>
        <h2>{t('errorBoundary.title')}</h2>
        <p style={{ color: '#6b7280' }}>{t('errorBoundary.body')}</p>
        <button style={button} onClick={() => window.location.reload()}>
          {t('errorBoundary.reload')}
        </button>
      </div>
    </div>
  );
}

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
}

// Um novo deploy troca os hashes dos chunks; uma aba já aberta com o
// index.html antigo tenta buscar um chunk que não existe mais e recebe
// ChunkLoadError. Isso não é um bug do app — é resolvido com um reload,
// que busca o index.html atual com os hashes corretos. Recarrega uma
// única vez por sessão (sessionStorage) pra não entrar em loop caso o
// reload não resolva (aí sim é erro de verdade e mostramos o fallback).
const CHUNK_RELOAD_FLAG = 'chunk-error-reload-attempted';

function isChunkLoadError(error: Error): boolean {
  return (
    error?.name === 'ChunkLoadError' ||
    /Loading (chunk|CSS chunk) .* failed/i.test(error?.message || '')
  );
}

// Um reload simples não basta: /static/* é servido "immutable" por 1 ano, então
// se o navegador (ou o cache do service worker) guardou um HTML sob a URL de um
// chunk durante a janela de um deploy, ele é reutilizado pra sempre. Refaz o
// download ignorando o cache HTTP (sobrescreve a entrada) de TODOS os chunks que
// o bundle principal referencia — não só o que falhou, senão o próximo chunk
// envenenado derruba a app de novo — e descarta o cache do service worker.
const CHUNK_MAP = /"static\/(js|css)\/"\s*\+\s*\w+\s*\+\s*"\."\s*\+\s*(\{[^}]*\})\[\w+\]\s*\+\s*"\.chunk\.(?:js|css)"/g;

async function chunkUrlsFromMainBundle(): Promise<string[]> {
  const main = Array.from(document.scripts).map((s) => s.src).find((u) => /\/static\/js\/main\.[a-z0-9]+\.js/.test(u));
  if (!main) return [];
  const text = await (await fetch(main)).text();
  const urls: string[] = [];
  for (const m of Array.from(text.matchAll(CHUNK_MAP))) {
    for (const [, id, hash] of Array.from(m[2].matchAll(/(\d+):"([a-f0-9]+)"/g))) {
      urls.push(`${window.location.origin}/static/${m[1]}/${id}.${hash}.chunk.${m[1]}`);
    }
  }
  return urls;
}

async function healChunkCaches(error: Error): Promise<void> {
  try {
    const failed = Array.from((error?.message || '').matchAll(/https?:\/\/\S+?\.(?:js|css)/g), (m) => m[0]);
    const all = Array.from(new Set([...failed, ...(await chunkUrlsFromMainBundle().catch(() => []))]));
    await Promise.all(all.map((u) => fetch(u, { cache: 'reload' }).catch(() => undefined)));
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* best effort — the reload below still runs */
  }
}

class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('ErrorBoundary caught:', error, errorInfo);

    if (isChunkLoadError(error) && !sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
      sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
      healChunkCaches(error).finally(() => window.location.reload());
    }
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback />;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
